import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AbstractService } from './abstract-service';
import {
  SolBanner,
  SolBannerAction,
  SolBannerLevel,
  SolBannerMood,
  SolBannerPlacement,
} from '../models/core/sol-banner';
import { SolText } from '../models/core/sol-text';

export interface BannerConfig {
  key: string;
  mood?: SolBannerMood;
  heading?: SolText;
  description?: SolText;
  action?: SolBannerAction;
  dismissible?: boolean;
}

export interface BannerEntry {
  readonly owner: string;
  readonly key: string;
  readonly id: string;
  readonly level: SolBannerLevel;
  readonly placement: SolBannerPlacement;
  readonly dismissRequested: boolean;
  readonly banner: SolBanner;
}

export interface BannerEvent {
  readonly owner: string;
  readonly key: string;
}

/**
 * The published banners, which surface each belongs to, and where the app strip carrying its own sits.
 *
 * A banner names its level: `app` puts it in the shell strip above the navigation, `page` in the page
 * canvas. The two are held here together and read apart as `appEntries` and `pageEntries`, so each
 * surface renders only what was published for it.
 *
 * A publisher says where its own banners belong and the surface resolves one answer for the strip from the
 * app-level entries alone — a page banner sits in the canvas and covers nothing, so it neither settles the
 * strip nor takes on the close a covering strip obliges. The strip has a single position however many
 * features are holding it: it covers the app only while everyone
 * holding it asked to cover, since one publisher must not be able to pin another's banner over the
 * navigation and pushing the app down is the answer that costs a reader nothing.
 *
 * Covering then forces the close affordance on, whatever the publisher asked for — a banner sitting over
 * the app's own navigation that cannot be closed cannot be got past. A banner that pushes the app down
 * covers nothing, so there the publisher decides, and by default it stays until the side that raised it
 * takes it down.
 *
 * An entry holds both sides of that: `placement` and `dismissRequested` are what its publisher asked for,
 * `banner.dismissible` is what the strip settled on. The request is what each entry keeps, and every state
 * recomputes what renders from it, so a banner that only carried a close because the strip was covering
 * loses it again once another owner settles the strip down onto the page — the obligation lasts exactly as
 * long as the covering that earned it, rather than outliving it on a banner that covers nothing.
 *
 * While the strip does cover, a publisher that refused a close still gets one, and closing it still
 * reports on `closed$`: the reader really did dismiss that banner, and the alternative is a banner they
 * cannot get past.
 */
export class BannerState {
  private constructor(
    public readonly entries: readonly BannerEntry[],
    public readonly appEntries: readonly BannerEntry[],
    public readonly pageEntries: readonly BannerEntry[],
    public readonly placement: SolBannerPlacement,
  ) {}

  public static of(): BannerState {
    return BannerState.ofEntries([]);
  }

  public withPublished(
    owner: string,
    configs: readonly BannerConfig[],
    placement: SolBannerPlacement = 'inline',
    level: SolBannerLevel = 'app',
  ): BannerState {
    const published = BannerState.toEntries(owner, configs, placement, level);
    const held = this.entries.findIndex(entry => entry.owner === owner);
    if (held === -1) {
      return BannerState.ofEntries([...this.entries, ...published]);
    }
    const others = this.entries.filter(entry => entry.owner !== owner);
    return BannerState.ofEntries([...others.slice(0, held), ...published, ...others.slice(held)]);
  }

  public withCleared(owner: string): BannerState {
    return BannerState.ofEntries(this.entries.filter(entry => entry.owner !== owner));
  }

  public withClosed(id: string): BannerState {
    return BannerState.ofEntries(this.entries.filter(entry => entry.id !== id));
  }

  public entryOf(owner: string, key: string): BannerEntry | undefined {
    return this.entries.find(entry => entry.owner === owner && entry.key === key);
  }

  private static ofEntries(entries: readonly BannerEntry[]): BannerState {
    const appEntries = entries.filter(entry => entry.level === 'app');
    const placement = BannerState.toPlacement(appEntries);
    const resolved = entries.map(entry => BannerState.toResolved(entry, placement));
    return new BannerState(
      resolved,
      resolved.filter(entry => entry.level === 'app'),
      resolved.filter(entry => entry.level === 'page'),
      placement,
    );
  }

  private static toPlacement(appEntries: readonly BannerEntry[]): SolBannerPlacement {
    const covering = appEntries.length > 0 && appEntries.every(entry => entry.placement === 'overlay');
    return covering ? 'overlay' : 'inline';
  }

  private static toResolved(entry: BannerEntry, placement: SolBannerPlacement): BannerEntry {
    const covered = placement === 'overlay' && entry.level === 'app';
    const dismissible = covered || entry.dismissRequested;
    if (entry.banner.dismissible === dismissible) {
      return entry;
    }
    return { ...entry, banner: entry.banner.withDismissible(dismissible) };
  }

  private static toEntries(
    owner: string,
    configs: readonly BannerConfig[],
    placement: SolBannerPlacement,
    level: SolBannerLevel,
  ): BannerEntry[] {
    const keys = configs.map(config => config.key);
    const unique = configs.filter((config, index) => keys.indexOf(config.key) === index);
    const duplicated = keys.filter((key, index) => keys.indexOf(key) === index && keys.lastIndexOf(key) !== index);
    if (duplicated.length > 0) {
      console.warn(`BannerService: '${owner}' published a duplicate banner key:`, duplicated.join(', '));
    }
    return unique.map(config => BannerState.toEntry(owner, config, placement, level));
  }

  private static toEntry(
    owner: string,
    config: BannerConfig,
    placement: SolBannerPlacement,
    level: SolBannerLevel,
  ): BannerEntry {
    const dismissible = config.dismissible ?? false;
    const banner = SolBanner.of()
      .withMood(config.mood ?? 'info')
      .withHeading(config.heading ?? SolText.empty())
      .withDescription(config.description ?? SolText.empty())
      .withAction(config.action)
      .withRendered(true);
    return {
      owner,
      level,
      placement,
      dismissRequested: dismissible,
      key: config.key,
      id: `${owner}\u0000${config.key}`,
      banner,
    };
  }
}

/**
 * Holds the banners the surfaces render: `sol-banner-host` pages the app strip through its entries one at
 * a time, and `sol-page-banner-host` stacks the page ones. The whole list is published here and each host
 * decides what of its own level is on screen. Each publisher owns its own
 * slice: publishing replaces that owner's banners and leaves every other owner's standing, so two
 * features can hold the surface at once without wiping each other. Publishing says where those banners
 * belong as well as what they say, defaulting to pushing the app down; `BannerState` resolves the one
 * position the shared strip can have, and what that position obliges of the close affordance.
 *
 * `close` is the user dismissing a banner and reports it on `closed$`; `remove` takes one down without
 * claiming the user did. Pressing an action reports the owner and the caller-supplied key, which stay
 * opaque here.
 */
@Injectable({ providedIn: 'root' })
export class BannerService extends AbstractService<BannerState> {
  private readonly closedSubject = new Subject<BannerEvent>();
  private readonly actionPressedSubject = new Subject<BannerEvent>();

  public readonly closed$: Observable<BannerEvent> = this.closedSubject.asObservable();
  public readonly actionPressed$: Observable<BannerEvent> = this.actionPressedSubject.asObservable();

  constructor() {
    super(BannerState.of());
  }

  public publish(
    owner: string,
    configs: readonly BannerConfig[],
    placement?: SolBannerPlacement,
    level?: SolBannerLevel,
  ): void {
    this.update(state => state.withPublished(owner, configs, placement, level));
  }

  public clear(owner: string): void {
    this.update(state => state.withCleared(owner));
  }

  public close(owner: string, key: string): void {
    if (!this.remove(owner, key)) {
      return;
    }
    this.closedSubject.next({ owner, key });
  }

  public remove(owner: string, key: string): boolean {
    const entry = this.state.entryOf(owner, key);
    if (entry === undefined) {
      return false;
    }
    this.update(state => state.withClosed(entry.id));
    return true;
  }

  public pressAction(owner: string, key: string): void {
    if (this.state.entryOf(owner, key) === undefined) {
      return;
    }
    this.actionPressedSubject.next({ owner, key });
  }
}
